import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Calendar, Clock, CheckCircle, XCircle, Loader2, Users,
  Palmtree, Thermometer, Baby, AlertCircle,
  CalendarDays, UserCheck, TrendingUp
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const LEAVE_TYPES = [
  { value: 'Annual Leave', icon: Palmtree, color: 'bg-green-100 text-green-800' },
  { value: 'Sick Leave', icon: Thermometer, color: 'bg-red-100 text-red-800' },
  { value: 'Maternity Leave', icon: Baby, color: 'bg-pink-100 text-pink-800' },
  { value: 'Paternity Leave', icon: Baby, color: 'bg-blue-100 text-blue-800' },
  { value: 'Unpaid Leave', icon: AlertCircle, color: 'bg-gray-100 text-gray-800' },
  { value: 'Other', icon: AlertCircle, color: 'bg-gray-100 text-gray-800' },
];

const LeaveManagement = () => {
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [teamRequests, setTeamRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [approvalType, setApprovalType] = useState('hr'); // 'hr' or 'manager'

  const { getAuthHeaders, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }

      const [summaryRes, requestsRes, teamRes] = await Promise.all([
        fetch(`${API_URL}/api/leave/office-summary`, { headers }),
        fetch(`${API_URL}/api/leave/office-requests`, { headers }),
        fetch(`${API_URL}/api/leave/team-requests`, { headers })
      ]);

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (requestsRes.ok) setRequests(await requestsRes.json());
      if (teamRes.ok) setTeamRequests(await teamRes.json());
    } catch (error) {
      console.error('Error fetching leave data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openApproveModal = (request, type = 'hr') => {
    setSelectedRequest(request);
    setApprovalType(type);
    setShowApproveModal(true);
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    try {
      const headers = getAuthHeaders();
      const endpoint = approvalType === 'manager' 
        ? `${API_URL}/api/leave/request/${selectedRequest.id}/manager-approve`
        : `${API_URL}/api/leave/request/${selectedRequest.id}/approve`;
      
      const res = await fetch(endpoint, {
        method: 'PUT',
        headers
      });
      if (res.ok) {
        setShowApproveModal(false);
        await fetchData();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setProcessing(false);
    }
  };

  const openRejectModal = (request, type = 'hr') => {
    setSelectedRequest(request);
    setApprovalType(type);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    try {
      const headers = getAuthHeaders();
      const endpoint = approvalType === 'manager'
        ? `${API_URL}/api/leave/request/${selectedRequest.id}/manager-reject?rejection_reason=${encodeURIComponent(rejectionReason)}`
        : `${API_URL}/api/leave/request/${selectedRequest.id}/reject?rejection_reason=${encodeURIComponent(rejectionReason)}`;
      
      const res = await fetch(endpoint, { method: 'PUT', headers });
      if (res.ok) {
        setShowRejectModal(false);
        await fetchData();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setProcessing(false);
    }
  };

  // Check if a request is in my team (I'm the line manager)
  const isMyTeamRequest = (requestId) => {
    return teamRequests.some(r => r.id === requestId);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Pending Manager': 'bg-orange-100 text-orange-800 border border-orange-300',
      'Pending HR': 'bg-amber-100 text-amber-800 border border-amber-300',
      'Approved': 'bg-green-100 text-green-800 border border-green-300',
      'Rejected': 'bg-red-100 text-red-800 border border-red-300',
      'Cancelled': 'bg-gray-100 text-gray-700 border border-gray-300',
      // Legacy status mapping
      'Pending': 'bg-yellow-100 text-yellow-800 border border-yellow-300'
    };
    const icons = {
      'Pending Manager': Clock,
      'Pending HR': Clock,
      'Approved': CheckCircle,
      'Rejected': XCircle,
      'Cancelled': XCircle,
      'Pending': Clock
    };
    const Icon = icons[status] || Clock;
    return (
      <Badge className={`flex items-center gap-1 font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const getLeaveTypeInfo = (type) => {
    return LEAVE_TYPES.find(t => t.value === type) || LEAVE_TYPES[0];
  };

  const filteredRequests = requests.filter(r => {
    if (activeTab === 'pending') return r.status === 'Pending HR' || r.status === 'Pending Manager' || r.status === 'Pending';
    if (activeTab === 'approved') return r.status === 'Approved';
    if (activeTab === 'rejected') return r.status === 'Rejected';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leave-management-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Leave Management</h1>
        <p className="text-gray-600 dark:text-gray-400">Manage employee leave requests</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-700">Pending</p>
                <p className="text-2xl font-bold text-yellow-800">{summary?.pending_requests || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Approved</p>
                <p className="text-2xl font-bold text-green-800">{summary?.approved_requests || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700">Rejected</p>
                <p className="text-2xl font-bold text-red-800">{summary?.rejected_requests || 0}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">On Leave Today</p>
                <p className="text-2xl font-bold text-blue-800">{summary?.employees_on_leave_today || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700">Upcoming</p>
                <p className="text-2xl font-bold text-purple-800">{summary?.upcoming_leave_count || 0}</p>
              </div>
              <CalendarDays className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* On Leave Today & Upcoming */}
      {(summary?.on_leave_today?.length > 0 || summary?.upcoming_leave?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          {summary?.on_leave_today?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-600" />
                  On Leave Today ({summary.on_leave_today.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {summary.on_leave_today.map(emp => {
                    const typeInfo = getLeaveTypeInfo(emp.leave_type);
                    const TypeIcon = typeInfo.icon;
                    return (
                      <div key={emp.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center gap-2">
                          <TypeIcon className={`h-4 w-4 ${typeInfo.color.split(' ')[1]}`} />
                          <span className="font-medium">{emp.employee_name}</span>
                        </div>
                        <span className="text-sm text-gray-500">{emp.leave_type}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
          {summary?.upcoming_leave?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-purple-600" />
                  Upcoming Leave (Next 7 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {summary.upcoming_leave.map(emp => (
                    <div key={emp.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div>
                        <span className="font-medium">{emp.employee_name}</span>
                        <p className="text-xs text-gray-500">{emp.leave_type} - {emp.days_requested} days</p>
                      </div>
                      <span className="text-sm text-gray-500">
                        {new Date(emp.start_date).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Leave Requests Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">
            Pending ({requests.filter(r => r.status === 'Pending HR' || r.status === 'Pending Manager' || r.status === 'Pending').length})
          </TabsTrigger>
          <TabsTrigger value="approved">
            Approved ({requests.filter(r => r.status === 'Approved').length})
          </TabsTrigger>
          <TabsTrigger value="rejected">
            Rejected ({requests.filter(r => r.status === 'Rejected').length})
          </TabsTrigger>
          <TabsTrigger value="all">All ({requests.length})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <Card>
            <CardContent className="p-6">
              {filteredRequests.length > 0 ? (
                <div className="space-y-4">
                  {filteredRequests.map(request => {
                    const typeInfo = getLeaveTypeInfo(request.leave_type);
                    const TypeIcon = typeInfo.icon;
                    return (
                      <div key={request.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-lg ${typeInfo.color.split(' ')[0]}`}>
                            <TypeIcon className={`h-6 w-6 ${typeInfo.color.split(' ')[1]}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold">{request.employee_name}</p>
                              {getStatusBadge(request.status)}
                            </div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {request.leave_type}
                            </p>
                            <p className="text-sm text-gray-500">
                              {new Date(request.start_date).toLocaleDateString()} - {new Date(request.end_date).toLocaleDateString()}
                              <span className="ml-2 font-medium">({request.days_requested} days)</span>
                            </p>
                            {request.reason && (
                              <p className="text-sm text-gray-600 mt-1 italic">"{request.reason}"</p>
                            )}
                            {request.rejection_reason && (
                              <p className="text-sm text-red-600 mt-1">Reason: {request.rejection_reason}</p>
                            )}
                            {request.manager_rejection_reason && (
                              <p className="text-sm text-red-600 mt-1">Manager Reason: {request.manager_rejection_reason}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {request.status === 'Pending HR' && (
                            <>
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => openApproveModal(request, 'hr')}
                                disabled={processing}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => openRejectModal(request, 'hr')}
                                disabled={processing}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          {request.status === 'Pending Manager' && isMyTeamRequest(request.id) && (
                            <>
                              <Button 
                                size="sm" 
                                className="bg-blue-600 hover:bg-blue-700"
                                onClick={() => openApproveModal(request, 'manager')}
                                disabled={processing}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Manager Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => openRejectModal(request, 'manager')}
                                disabled={processing}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          {request.status === 'Pending Manager' && !isMyTeamRequest(request.id) && (
                            <span className="text-xs text-orange-600 font-medium">
                              Awaiting manager approval
                            </span>
                          )}
                          {request.status === 'Approved' && (
                            <div className="text-xs text-gray-500">
                              {request.manager_approved_by_name && (
                                <p>Manager: {request.manager_approved_by_name}</p>
                              )}
                              {request.approved_by_name && (
                                <p>HR: {request.approved_by_name}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No {activeTab !== 'all' ? activeTab : ''} leave requests</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {approvalType === 'manager' ? 'Reject Leave Request (Manager)' : 'Reject Leave Request (HR)'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Rejecting leave request for <strong>{selectedRequest?.employee_name}</strong>
            </p>
            <div>
              <Label>Rejection Reason (Optional)</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Provide a reason for rejection..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={processing}>
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Modal */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {approvalType === 'manager' ? 'Manager Approval' : 'HR Final Approval'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className={`p-4 rounded-lg border ${approvalType === 'manager' ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-sm ${approvalType === 'manager' ? 'text-blue-800' : 'text-green-800'}`}>
                {approvalType === 'manager' 
                  ? 'You are approving this request as the Line Manager. It will then move to HR for final approval.'
                  : 'You are about to give final HR approval for this leave request:'}
              </p>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-500">Employee</span>
                <span className="font-medium">{selectedRequest?.employee_name}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-500">Leave Type</span>
                <span className="font-medium">{selectedRequest?.leave_type}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-500">Duration</span>
                <span className="font-medium">{selectedRequest?.days_requested} day(s)</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-500">From</span>
                <span className="font-medium">{selectedRequest?.start_date ? new Date(selectedRequest.start_date).toLocaleDateString() : '-'}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-500">To</span>
                <span className="font-medium">{selectedRequest?.end_date ? new Date(selectedRequest.end_date).toLocaleDateString() : '-'}</span>
              </div>
            </div>
            {selectedRequest?.reason && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Reason</p>
                <p className="text-sm">{selectedRequest.reason}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveModal(false)}>Cancel</Button>
            <Button 
              className={approvalType === 'manager' ? "bg-blue-600 hover:bg-blue-700" : "bg-green-600 hover:bg-green-700"} 
              onClick={handleApprove} 
              disabled={processing}
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {approvalType === 'manager' ? 'Manager Approve' : 'HR Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveManagement;
